import React, { useEffect, useMemo, useState } from "react";
import ErpCardRoutingPanel, {
  ErpCardActiveGroup,
  ErpCardMapping,
  ErpCardMasterSnapshot,
  ErpCardOperationalContract,
  ErpPrepareOnlyEvidence,
  ErpOwnerRoutingPreview,
} from "./ErpCardRoutingPanel";
import ErpOperationsPanel, { ErpOperationsSnapshot } from "./ErpOperationsPanel";

export type ErpBridgeBatch = {
  batchId: string;
  status: string;
  createdAt: string;
  createdBy: string;
  sourceCount: number;
  sourceAmount: number;
  exportCount: number;
  exportAmount: number;
  erpImportCount: number;
  erpImportAmount: number;
  verifiedCount: number;
  verifiedAmount: number;
  fileName: string;
  fileHash: string;
  agentId: string;
  agentHost: string;
  importStartedAt: string;
  importCompletedAt: string;
  verifiedAt: string;
  errorCode: string;
  errorMessage: string;
  mappingVersion: string;
  mappingStatus: string;
  bridgeMode: string;
  eventCount?: number;
  approvalCount?: number;
  cancellationCount?: number;
  positiveAmount?: number;
  negativeAmount?: number;
  netAmount?: number;
  activeReceiptId?: string;
  activeReceiptIdMasked?: string;
  receiptStatus?: string;
  events?: ErpBridgeEvent[];
  reconciliation: {
    sourceCount: number;
    sourceAmount: number;
    exportCount: number;
    exportAmount: number;
    countMatch?: boolean;
    amountMatch?: boolean;
    sourceEventCount?: number;
    sourcePositiveAmount?: number;
    sourceNegativeAmount?: number;
    sourceNetAmount?: number;
    xlsEventCount?: number;
    xlsPositiveAmount?: number;
    xlsNegativeAmount?: number;
    xlsNetAmount?: number;
    eventCountMatch?: boolean;
    positiveAmountMatch?: boolean;
    negativeAmountMatch?: boolean;
    netAmountMatch?: boolean;
  };
};

export type ErpBridgeEvent = {
  eventId: string;
  eventType: "APPROVAL" | "CANCELLATION" | string;
  paymentId: string;
  occurredAt: string;
  vendorId?: string;
  vendorCode: string;
  vendorName: string;
  approvalCode: string;
  approvalTransactionId?: string;
  cardIssuerCode?: string;
  cardIssuerName?: string;
  grossAmount?: number;
  signedErpAmount: number;
  balanceAfter?: number;
  erpStatus?: string;
  erpBatchId?: string;
  erpImportGroupId?: string;
  entityId?: "INCHEON" | "AGRICULTURAL" | string;
  erpEntityName?: string;
  targetErpCardName?: string;
};

export type ErpBridgeSummary = {
  bridgeMode: string;
  mapping: { version: string; status: string };
  pending: {
    count: number;
    amount: number;
    eventIds: string[];
    events: ErpBridgeEvent[];
    paymentIds?: string[];
    approvalCount?: number;
    cancellationCount?: number;
    positiveAmount?: number;
    negativeAmount?: number;
    netAmount?: number;
    truncated: boolean;
    batchCreationBlocked: boolean;
  };
  batches: ErpBridgeBatch[];
  batchesTruncated?: boolean;
};

export type ErpManualExportContract = {
  groupId: string;
  occurredOn: string;
  entityId: "INCHEON" | "AGRICULTURAL";
  erpEntityName: string;
  targetErpCardName: string;
  eventCount: number;
  netAmount: number;
  fileName: string;
  fileHash: string;
  materializationContractHash: string;
  schemaVersion: "CN_AX_ERP_XLS_MATERIALIZATION_CONTRACT_V1";
  requiredMaterializer: "XLWT_JEGOCS_V1";
  status: "MATERIALIZATION_REQUIRED" | "FILE_CREATED";
};

type CreateResult = {
  batch: ErpBridgeBatch;
  erpEventIds?: string[];
  files?: never[];
  manualExports?: ErpManualExportContract[];
  groups?: Array<{
    groupId: string;
    occurredOn: string;
    entityId: "INCHEON" | "AGRICULTURAL";
    erpEntityName: string;
    targetErpCardName: string;
    eventCount: number;
  }>;
  mappingRequired?: Array<{ eventId: string; sourceCardName: string; reasonCode: string }>;
  reusedExistingBatch?: boolean;
};

type Props = {
  loading?: boolean;
  error?: string;
  summary: ErpBridgeSummary | null;
  onReload: () => Promise<unknown>;
  onCreateBatch: (erpEventIds: string[]) => Promise<CreateResult>;
  onNotify?: (message: string) => void;
  cardRoutingConfiguration?: {
    mappings?: ErpCardMapping[];
    erpCardMaster?: ErpCardMasterSnapshot | null;
    activeGroups?: ErpCardActiveGroup[];
    groups?: ErpCardActiveGroup[];
    operationalContract?: ErpCardOperationalContract;
    ownerRulePreview?: ErpOwnerRoutingPreview | null;
    prepareOnlyEvidence?: ErpPrepareOnlyEvidence | null;
  } | null;
  cardRoutingLoading?: boolean;
  cardRoutingSaving?: boolean;
  cardRoutingError?: string;
  onReloadCardRouting?: () => Promise<unknown>;
  onCreateCardMapping?: (mapping: {
    sourceCardName: string;
    targetErpCardName: string;
    enabled: boolean;
  }) => Promise<unknown>;
  onUpdateCardMapping?: (
    mappingId: string,
    patch: { sourceCardName: string; targetErpCardName: string }
  ) => Promise<unknown>;
  onDeleteCardMapping?: (mappingId: string) => Promise<unknown>;
  onToggleCardMapping?: (mappingId: string, enabled: boolean) => Promise<unknown>;
  operationsSnapshot?: ErpOperationsSnapshot | null;
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: "ERP 미반영",
  EXPORT_RESERVED: "전송 예약",
  FILE_CREATED: "파일 생성",
  IMPORT_STARTED: "ERP 전송 중",
  IMPORT_SUCCEEDED: "가져오기 성공",
  VERIFY_REQUIRED: "확인 필요",
  VERIFIED: "ERP 검증 완료",
  FAILED: "실패",
  PRE_IMPORT_FAILED: "전송 전 실패",
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  APPROVAL: "승인",
  CANCELLATION: "취소",
};

const ENTITY_LABELS: Record<ErpManualExportContract["entityId"], string> = {
  INCHEON: "인천",
  AGRICULTURAL: "농법",
};

const REUSABLE_DOWNLOAD_STATUSES = new Set(["MATERIALIZATION_REQUIRED", "FILE_CREATED"]);

const formatMoney = (value: number) => `${Number(value || 0).toLocaleString("ko-KR")}원`;

const formatDateTime = (value = "") => {
  const normalizedValue = /^\d{14}$/.test(value)
    ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}T${value.slice(
        8,
        10
      )}:${value.slice(10, 12)}:${value.slice(12, 14)}+09:00`
    : value;
  const timestamp = Date.parse(normalizedValue);
  if (!Number.isFinite(timestamp)) return "-";
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(timestamp));
};

const getBatchAmounts = (batch: ErpBridgeBatch) => ({
  approvalAmount: Number(batch.positiveAmount ?? batch.sourceAmount) || 0,
  cancellationAmount: Math.abs(Number(batch.negativeAmount) || 0),
  netAmount: Number(batch.netAmount ?? batch.sourceAmount) || 0,
  erpTransferredAmount: Number(batch.erpImportAmount) || 0,
});

const getReceiptLabel = (batch: ErpBridgeBatch) => {
  if (batch.bridgeMode === "DRY_RUN" || batch.receiptStatus === "NOT_ISSUED_DRY_RUN") {
    return "DRY_RUN 미발급";
  }
  return batch.receiptStatus || "미발급";
};

const ErpBridgePanel = ({
  loading = false,
  error = "",
  summary,
  onReload,
  onCreateBatch,
  onNotify,
  cardRoutingConfiguration,
  cardRoutingLoading = false,
  cardRoutingSaving = false,
  cardRoutingError = "",
  onReloadCardRouting,
  onCreateCardMapping,
  onUpdateCardMapping,
  onDeleteCardMapping,
  onToggleCardMapping,
  operationsSnapshot,
}: Props) => {
  const [creating, setCreating] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  const [eventSearch, setEventSearch] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("ALL");
  const [manualExports, setManualExports] = useState<ErpManualExportContract[]>([]);

  useEffect(() => {
    void onReload().catch(() => undefined);
    if (onReloadCardRouting) {
      void onReloadCardRouting().catch(() => undefined);
    }
    // The parent owns refresh identity; mount-triggered load must run only once.
  }, []);

  const batches = summary?.batches || [];
  const pendingEvents = summary?.pending.events || [];
  const pendingEventIds = summary?.pending.eventIds || pendingEvents.map((event) => event.eventId);
  const pendingEventKey = pendingEventIds.join("\u0000");
  const pendingEventIdSet = useMemo(() => new Set(pendingEventIds), [pendingEventKey]);
  const ledgerEvents = useMemo(() => {
    const uniqueEvents = new Map<string, ErpBridgeEvent>();
    batches.forEach((batch) =>
      (batch.events || []).forEach((event) => uniqueEvents.set(event.eventId, event))
    );
    pendingEvents.forEach((event) => uniqueEvents.set(event.eventId, event));
    return Array.from(uniqueEvents.values()).sort(
      (left, right) =>
        String(right.occurredAt).localeCompare(String(left.occurredAt)) ||
        left.eventId.localeCompare(right.eventId)
    );
  }, [batches, pendingEvents]);
  const pendingEventAmounts = useMemo(
    () =>
      pendingEvents.reduce(
        (summary, event) => {
          const amount = Number(event.signedErpAmount) || 0;
          if (amount > 0) summary.approvalAmount += amount;
          if (amount < 0) summary.cancellationAmount += Math.abs(amount);
          summary.netAmount += amount;
          return summary;
        },
        { approvalAmount: 0, cancellationAmount: 0, netAmount: 0 }
      ),
    [pendingEvents]
  );

  useEffect(() => {
    const availableIds = new Set(pendingEventIds);
    setSelectedEventIds((current) => {
      const retained = current.filter((eventId) => availableIds.has(eventId));
      return retained.length > 0 ? retained : pendingEventIds.slice(0, 100);
    });
  }, [pendingEventKey]);

  const selectedBatch = useMemo(
    () =>
      batches.find((batch) => batch.batchId === selectedBatchId) || batches[0] || null,
    [batches, selectedBatchId]
  );
  const selectedBatchAmounts = selectedBatch ? getBatchAmounts(selectedBatch) : null;
  const statusCounts = useMemo(
    () =>
      batches.reduce<Record<string, number>>((counts, batch) => {
        counts[batch.status] = (counts[batch.status] || 0) + 1;
        return counts;
      }, {}),
    [batches]
  );
  const filteredEvents = useMemo(() => {
    const normalizedSearch = eventSearch.trim().toLocaleLowerCase("ko-KR");
    return ledgerEvents.filter((event) => {
      if (eventTypeFilter !== "ALL" && event.eventType !== eventTypeFilter) return false;
      if (!normalizedSearch) return true;
      return [
        event.eventId,
        event.paymentId,
        event.vendorName,
        event.vendorCode,
        event.approvalCode,
        event.approvalTransactionId,
      ].some((value) => String(value || "").toLocaleLowerCase("ko-KR").includes(normalizedSearch));
    });
  }, [eventSearch, eventTypeFilter, ledgerEvents]);
  const selectedEventIdSet = useMemo(() => new Set(selectedEventIds), [selectedEventIds]);
  const selectableEventIdSet = useMemo(() => new Set(ledgerEvents
    .filter((event) =>
      pendingEventIdSet.has(event.eventId) ||
      (REUSABLE_DOWNLOAD_STATUSES.has(String(event.erpStatus || "").toUpperCase()) &&
        event.erpBatchId !== "B260831-001")
    )
    .map((event) => event.eventId)), [ledgerEvents, pendingEventIdSet]);
  const createBlocked =
    creating ||
    loading ||
    selectedEventIds.length === 0 ||
    selectedEventIds.length > 100 ||
    summary?.pending.batchCreationBlocked === true;
  const hasOperationsSnapshot =
    Boolean(operationsSnapshot?.capturedAt) &&
    Array.isArray(operationsSnapshot?.services) &&
    Array.isArray(operationsSnapshot?.schedulerHistory);

  const toggleEvent = (eventId: string) => {
    setSelectedEventIds((current) =>
      current.includes(eventId)
        ? current.filter((selectedId) => selectedId !== eventId)
        : current.length < 100
          ? [...current, eventId]
          : current
    );
  };

  const selectVisibleEvents = () => {
    const visibleIds = filteredEvents
      .filter((event) => selectableEventIdSet.has(event.eventId))
      .map((event) => event.eventId);
    setSelectedEventIds(visibleIds.slice(0, 100));
  };

  const createBatch = async () => {
    if (createBlocked) return;
    setCreating(true);
    setManualExports([]);
    try {
      const result = await onCreateBatch(selectedEventIds);
      setSelectedBatchId(result.batch.batchId);
      setManualExports(result.manualExports || []);
      const eventCount = result.erpEventIds?.length || selectedEventIds.length;
      onNotify?.(
        `${result.batch.batchId} Event ${eventCount}건을 거래일자+사업장+ERP 카드별 ${result.groups?.length || result.manualExports?.length || 0}개 XLWT 생성 계약으로 준비했습니다.${result.reusedExistingBatch ? " 기존 Batch/Group을 재사용했습니다." : ""} 회사 PC Agent에서 수동 업로드용 XLS를 생성하세요. Mapping 대기 ${result.mappingRequired?.length || 0}건이며 실제 천년경영 가져오기는 실행하지 않았습니다.`
      );
      await onReload();
    } catch (createError: any) {
      onNotify?.(createError?.message || "ERP DRY_RUN Batch를 만들지 못했습니다.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <section className="space-y-4" aria-labelledby="erp-bridge-title">
      <div className="border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase text-blue-700">CN AX Bridge</p>
            <h4 id="erp-bridge-title" className="mt-1 text-lg font-black text-slate-950">
              ERP 연계 현황
            </h4>
            <p className="mt-2 max-w-3xl text-xs font-bold leading-5 text-slate-600">
              BUYLINK 승인과 취소를 불변 Event로 분리하고 Mapping Snapshot을 적용한 Parent Batch와
              사업장과 Target ERP 카드별 Group을 구성하고 검증된 XLWT_JEGOCS_V1 생성 계약을 제공합니다.
              웹에서는 XLS bytes를 만들거나 다운로드하지 않으며 Receipt 발급과 실제 가져오기는 실행하지 않습니다.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void onReload().catch(() => undefined)}
              disabled={loading}
              className="min-h-[42px] border border-slate-300 bg-white px-4 text-xs font-black text-slate-800 disabled:opacity-50"
            >
              {loading ? "조회 중..." : "다시 조회"}
            </button>
            <button
              type="button"
              onClick={() => void createBatch()}
              disabled={createBlocked}
              className="min-h-[42px] bg-blue-700 px-4 text-xs font-black text-white disabled:bg-slate-300"
            >
              {creating
                ? "생성 중..."
                : `ERP 업로드용 .xls 생성 (${selectedEventIds.length} Event)`}
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-black">
          <span className="bg-slate-900 px-3 py-2 text-white">
            Mode {summary?.bridgeMode || "DRY_RUN"}
          </span>
          <span className="bg-amber-100 px-3 py-2 text-amber-950">
            Mapping {summary?.mapping.version || "ERP_DEPOSIT_V1_DRAFT"}
          </span>
          <span className="bg-amber-100 px-3 py-2 text-amber-950">
            {summary?.mapping.status || "CONFIGURATION_PENDING"}
          </span>
          <span className="bg-red-100 px-3 py-2 text-red-950">
            Receipt DRY_RUN 미발급 · 실행 차단
          </span>
        </div>

        {summary?.pending.truncated && (
          <div className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-xs font-black text-red-800">
            결제 조회가 5,000건에서 잘려 Batch 생성을 중단했습니다. 기간/페이지 기반 서버
            조회 계약을 보완한 뒤 다시 시도해야 합니다.
          </div>
        )}
        {pendingEventIds.length > 100 && !summary?.pending.truncated && (
          <div className="mt-4 border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-black text-amber-900">
            ERP 미반영 Event가 100건을 초과했습니다. 한 Batch에는 최대 100 Event만 선택할
            수 있습니다.
          </div>
        )}
        {error && (
          <div className="mt-4 border border-red-200 bg-red-50 px-4 py-3 text-xs font-black text-red-800">
            {error}
          </div>
        )}
      </div>

      {manualExports.length > 0 && (
        <section className="border border-blue-200 bg-blue-50 p-5" aria-labelledby="erp-download-files-title">
          <h5 id="erp-download-files-title" className="text-sm font-black text-blue-950">
            ERP 수동 업로드 XLS 생성 계약
          </h5>
          <p className="mt-2 text-xs font-bold leading-5 text-blue-900">
            MANUAL_ERP_UPLOAD_SHEETJS_FORBIDDEN · 회사 PC Agent의 "수동 업로드용 XLS 생성"에서
            XLWT_JEGOCS_V1으로 생성합니다. 웹 다운로드와 ERP 가져오기는 실행하지 않습니다.
          </p>
          <div className="mt-4 space-y-3">
            {manualExports.map((file) => (
              <article key={`${file.groupId}:${file.materializationContractHash}`} className="border border-blue-200 bg-white p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-black text-slate-950">
                      {file.occurredOn.replace(/-/g, "")} [{ENTITY_LABELS[file.entityId]}] {file.targetErpCardName} · {file.eventCount.toLocaleString("ko-KR")}건 · {formatMoney(file.netAmount)}
                    </p>
                    <p className="mt-1 break-all text-xs font-bold text-slate-700">{file.fileName}</p>
                    <p className="mt-1 font-mono text-[11px] font-bold text-slate-500">
                      Contract SHA-256 {file.materializationContractHash.slice(0, 12)}…
                    </p>
                  </div>
                  <span className="shrink-0 bg-amber-100 px-4 py-3 text-xs font-black text-amber-950">
                    {file.status === "FILE_CREATED" ? "Agent 생성 완료" : "Agent 생성 대기"}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
        {[
          ["ERP 미반영 Event", summary?.pending.count || 0, "bg-amber-50 text-amber-950"],
          ["파일 생성", statusCounts.FILE_CREATED || 0, "bg-blue-50 text-blue-950"],
          ["전송 중", statusCounts.IMPORT_STARTED || 0, "bg-violet-50 text-violet-950"],
          ["확인 필요", statusCounts.VERIFY_REQUIRED || 0, "bg-red-50 text-red-950"],
          ["검증 완료", statusCounts.VERIFIED || 0, "bg-emerald-50 text-emerald-950"],
        ].map(([label, value, color]) => (
          <div key={String(label)} className={`px-4 py-4 ${color}`}>
            <p className="text-[11px] font-bold">{label}</p>
            <strong className="mt-1 block text-xl font-black">
              {Number(value).toLocaleString("ko-KR")}
            </strong>
          </div>
        ))}
      </div>

      <section className="border border-slate-200 bg-white" aria-labelledby="erp-event-ledger-title">
        <div className="border-b border-slate-200 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h5 id="erp-event-ledger-title" className="text-sm font-black text-slate-950">
                Event Ledger
              </h5>
              <p className="mt-1 text-xs font-bold text-slate-600">
                승인과 취소를 독립 Event로 조회합니다. 미반영 Event만 선택할 수 있고,
                선택한 Event ID만 DRY_RUN Batch 생성 요청에 전달됩니다.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs font-black">
              <span className="bg-emerald-50 px-3 py-2 text-emerald-950">
                승인액 {formatMoney(summary?.pending.positiveAmount ?? pendingEventAmounts.approvalAmount)}
              </span>
              <span className="bg-red-50 px-3 py-2 text-red-950">
                취소액 {formatMoney(Math.abs(summary?.pending.negativeAmount ?? pendingEventAmounts.cancellationAmount))}
              </span>
              <span className="bg-blue-50 px-3 py-2 text-blue-950">
                Net {formatMoney(summary?.pending.netAmount ?? pendingEventAmounts.netAmount)}
              </span>
            </div>
          </div>

          <div className="mt-4 grid gap-2 md:grid-cols-[minmax(220px,1fr)_180px_auto]">
            <label className="text-xs font-black text-slate-700">
              Event 검색
              <input
                type="search"
                value={eventSearch}
                onChange={(event) => setEventSearch(event.target.value)}
                placeholder="Event ID, 결제 ID, 거래처, 승인번호"
                className="mt-1 min-h-[42px] w-full border border-slate-300 px-3 font-bold text-slate-900"
              />
            </label>
            <label className="text-xs font-black text-slate-700">
              Event 유형
              <select
                value={eventTypeFilter}
                onChange={(event) => setEventTypeFilter(event.target.value)}
                className="mt-1 min-h-[42px] w-full border border-slate-300 bg-white px-3 font-bold text-slate-900"
              >
                <option value="ALL">전체</option>
                <option value="APPROVAL">승인</option>
                <option value="CANCELLATION">취소</option>
              </select>
            </label>
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={selectVisibleEvents}
                disabled={filteredEvents.length === 0}
                className="min-h-[42px] border border-blue-300 bg-blue-50 px-3 text-xs font-black text-blue-900 disabled:opacity-40"
              >
                검색결과 선택
              </button>
              <button
                type="button"
                onClick={() => setSelectedEventIds([])}
                disabled={selectedEventIds.length === 0}
                className="min-h-[42px] border border-slate-300 px-3 text-xs font-black text-slate-700 disabled:opacity-40"
              >
                선택 해제
              </button>
            </div>
          </div>
          <p className="mt-3 text-xs font-black text-slate-600" role="status" aria-live="polite">
            검색 {filteredEvents.length.toLocaleString("ko-KR")}건 · 선택 {selectedEventIds.length}건
            {filteredEvents.length > 200 ? " · 화면에는 앞 200건만 표시" : ""}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1120px] w-full border-collapse text-left text-xs">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                {["선택/상태", "발생시간", "유형", "Event ID", "결제 ID", "거래처", "카드사", "승인번호", "ERP 반영액", "Batch"].map(
                  (header) => (
                    <th key={header} scope="col" className="border-b border-slate-200 px-3 py-3 font-black">
                      {header}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {filteredEvents.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center font-bold text-slate-500">
                    조건에 맞는 Event가 없습니다.
                  </td>
                </tr>
              ) : (
                filteredEvents.slice(0, 200).map((event) => {
                  const selectable = selectableEventIdSet.has(event.eventId);
                  const selected = selectable && selectedEventIdSet.has(event.eventId);
                  return (
                    <tr
                      key={event.eventId}
                      tabIndex={selectable ? 0 : -1}
                      aria-selected={selected}
                      onKeyDown={(keyboardEvent) => {
                        if (
                          selectable &&
                          (keyboardEvent.key === "Enter" || keyboardEvent.key === " ")
                        ) {
                          keyboardEvent.preventDefault();
                          toggleEvent(event.eventId);
                        }
                      }}
                      className={`border-b border-slate-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-600 ${
                        selected ? "bg-blue-50" : "hover:bg-slate-50"
                      }`}
                    >
                      <td className="px-3 py-3">
                        {selectable ? (
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleEvent(event.eventId)}
                            aria-label={`${event.eventId} Event 선택`}
                            className="h-4 w-4 accent-blue-700"
                          />
                        ) : (
                          <span className="font-black text-slate-500">
                            {event.erpStatus || "반영됨"}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 font-bold">{formatDateTime(event.occurredAt)}</td>
                      <td className={`px-3 py-3 font-black ${event.eventType === "CANCELLATION" ? "text-red-700" : "text-emerald-700"}`}>
                        {EVENT_TYPE_LABELS[event.eventType] || event.eventType}
                      </td>
                      <td className="max-w-[230px] break-all px-3 py-3 font-bold text-blue-800">
                        {event.eventId}
                      </td>
                      <td className="max-w-[160px] break-all px-3 py-3 font-bold">{event.paymentId}</td>
                      <td className="px-3 py-3 font-bold">
                        {event.vendorName || "-"} · {event.vendorCode || "-"}
                      </td>
                      <td className="px-3 py-3 font-bold">
                        {event.cardIssuerName || event.cardIssuerCode || "미확인"}
                      </td>
                      <td className="px-3 py-3 font-bold">{event.approvalCode || "-"}</td>
                      <td className={`px-3 py-3 font-black ${event.signedErpAmount < 0 ? "text-red-700" : "text-emerald-700"}`}>
                        {formatMoney(event.signedErpAmount)}
                      </td>
                      <td className="px-3 py-3 font-bold">{event.erpBatchId || "미반영"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,1fr)]">
        <div className="overflow-x-auto border border-slate-200 bg-white">
          <table className="min-w-[980px] w-full border-collapse text-left text-xs">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                {["Batch ID", "생성시간", "Event", "승인액", "취소액", "Net", "ERP전송액", "Receipt", "상태"].map(
                  (header) => (
                    <th key={header} scope="col" className="border-b border-slate-200 px-3 py-3 font-black">
                      {header}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {batches.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center font-bold text-slate-500">
                    아직 생성된 ERP Batch가 없습니다.
                  </td>
                </tr>
              ) : (
                batches.map((batch) => {
                  const amounts = getBatchAmounts(batch);
                  const selected = selectedBatch?.batchId === batch.batchId;
                  return (
                    <tr
                      key={batch.batchId}
                      tabIndex={0}
                      aria-selected={selected}
                      onClick={() => setSelectedBatchId(batch.batchId)}
                      onKeyDown={(keyboardEvent) => {
                        if (keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
                          keyboardEvent.preventDefault();
                          setSelectedBatchId(batch.batchId);
                        }
                      }}
                      className={`cursor-pointer border-b border-slate-100 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-600 ${
                        selected ? "bg-blue-50" : ""
                      }`}
                    >
                      <td className="px-3 py-3 font-black text-blue-800">{batch.batchId}</td>
                      <td className="px-3 py-3 font-bold">{formatDateTime(batch.createdAt)}</td>
                      <td className="px-3 py-3 font-bold">{batch.eventCount ?? batch.sourceCount}건</td>
                      <td className="px-3 py-3 font-black text-emerald-700">
                        {formatMoney(amounts.approvalAmount)}
                      </td>
                      <td className="px-3 py-3 font-black text-red-700">
                        {formatMoney(amounts.cancellationAmount)}
                      </td>
                      <td className="px-3 py-3 font-black">{formatMoney(amounts.netAmount)}</td>
                      <td className="px-3 py-3 font-black text-violet-700">
                        {formatMoney(amounts.erpTransferredAmount)}
                      </td>
                      <td className="px-3 py-3 font-black">{getReceiptLabel(batch)}</td>
                      <td className="px-3 py-3 font-black">
                        {STATUS_LABELS[batch.status] || batch.status || "-"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <aside className="border border-slate-200 bg-white p-5">
          <h5 className="text-sm font-black text-slate-950">Event Reconciliation</h5>
          {!selectedBatch || !selectedBatchAmounts ? (
            <p className="mt-4 text-xs font-bold text-slate-500">Batch를 선택해 대사결과를 확인하세요.</p>
          ) : (
            <>
              <p className="mt-1 text-xs font-black text-blue-800">{selectedBatch.batchId}</p>
              <div className="mt-4 space-y-2">
                {[
                  ["승인 Event", selectedBatch.approvalCount ?? selectedBatch.sourceCount, selectedBatchAmounts.approvalAmount],
                  ["취소 Event", selectedBatch.cancellationCount ?? 0, selectedBatchAmounts.cancellationAmount],
                  ["Net", selectedBatch.eventCount ?? selectedBatch.sourceCount, selectedBatchAmounts.netAmount],
                  ["XLS Net", selectedBatch.exportCount, selectedBatch.exportAmount],
                  ["ERP 전송", selectedBatch.erpImportCount, selectedBatchAmounts.erpTransferredAmount],
                ].map(([label, count, amount]) => (
                  <div key={String(label)} className="flex items-center justify-between bg-slate-50 px-3 py-3">
                    <span className="text-xs font-black text-slate-700">{label}</span>
                    <strong className="text-right text-xs font-black text-slate-950">
                      {Number(count).toLocaleString("ko-KR")}건<br />
                      {formatMoney(Number(amount))}
                    </strong>
                  </div>
                ))}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-center text-[11px] font-black">
                <span className={(selectedBatch.reconciliation.eventCountMatch ?? selectedBatch.reconciliation.countMatch) ? "bg-emerald-100 px-2 py-2 text-emerald-900" : "bg-red-100 px-2 py-2 text-red-900"}>
                  {(selectedBatch.reconciliation.eventCountMatch ?? selectedBatch.reconciliation.countMatch)
                    ? "EVENT↔XLS COUNT MATCH"
                    : "EVENT↔XLS COUNT STOP"}
                </span>
                <span className={(selectedBatch.reconciliation.netAmountMatch ?? selectedBatch.reconciliation.amountMatch) ? "bg-emerald-100 px-2 py-2 text-emerald-900" : "bg-red-100 px-2 py-2 text-red-900"}>
                  {(selectedBatch.reconciliation.netAmountMatch ?? selectedBatch.reconciliation.amountMatch)
                    ? "EVENT↔XLS NET MATCH"
                    : "EVENT↔XLS NET STOP"}
                </span>
              </div>
              <dl className="mt-4 space-y-2 text-xs">
                <div className="flex justify-between gap-3">
                  <dt className="font-bold text-slate-500">Mapping</dt>
                  <dd className="text-right font-black text-slate-800">{selectedBatch.mappingVersion}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="font-bold text-slate-500">Receipt</dt>
                  <dd className="text-right font-black text-red-800">
                    {getReceiptLabel(selectedBatch)} · 실행 차단
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="font-bold text-slate-500">Agent</dt>
                  <dd className="text-right font-black text-slate-800">
                    {selectedBatch.agentId || "확인대기"}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="font-bold text-slate-500">File</dt>
                  <dd className="max-w-[220px] break-all text-right font-black text-slate-800">
                    {selectedBatch.fileName || "-"}
                  </dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="font-bold text-slate-500">ERP 검증</dt>
                  <dd className="text-right font-black text-slate-800">
                    {selectedBatch.status === "VERIFIED" ? "완료" : "확인대기"}
                  </dd>
                </div>
              </dl>
            </>
          )}
        </aside>
      </div>
      <ErpCardRoutingPanel
        runtime={{
          mappings: cardRoutingConfiguration?.mappings || [],
          erpCardMaster: cardRoutingConfiguration?.erpCardMaster || null,
          activeGroups: cardRoutingConfiguration?.activeGroups || [],
          groupHistory: cardRoutingConfiguration?.groups || [],
          operationalContract: cardRoutingConfiguration?.operationalContract,
          ownerRulePreview: cardRoutingConfiguration?.ownerRulePreview || null,
          prepareOnlyEvidence: cardRoutingConfiguration?.prepareOnlyEvidence || null,
          parentBatches: batches.map((batch) => ({
            parentBatchId: batch.batchId,
            status: batch.status,
            eventCount: Number(batch.eventCount ?? batch.sourceCount) || 0,
            netAmount: Number(batch.netAmount ?? batch.sourceAmount) || 0,
            verifiedCount: Number(batch.verifiedCount) || 0,
            createdAt: batch.createdAt,
          })),
          pendingEvents,
          loading: cardRoutingLoading,
          saving: cardRoutingSaving,
          error:
            cardRoutingError ||
            (!cardRoutingConfiguration
              ? "ERP Card Routing/Vendor Field Contract 운영 payload 미연결"
              : ""),
          onReload: onReloadCardRouting,
          onCreateMapping: onCreateCardMapping,
          onUpdateMapping: onUpdateCardMapping,
          onDeleteMapping: onDeleteCardMapping,
          onToggleMapping: onToggleCardMapping,
          onNotify,
        }}
      />
      {hasOperationsSnapshot && operationsSnapshot ? (
        <ErpOperationsPanel snapshot={operationsSnapshot} />
      ) : <section className="border border-amber-200 bg-amber-50 p-5" aria-labelledby="erp-operations-unconnected-title">
        <h4 id="erp-operations-unconnected-title" className="text-sm font-black text-amber-950">
          ERP 운영현황 · 미연결
        </h4>
        <p className="mt-2 text-xs font-bold leading-5 text-amber-900">
          실제 Supervisor/Heartbeat/Scheduler payload가 이 관리자 화면에 연결되지 않았습니다. Synthetic 상태를
          운영값으로 표시하지 않으며 CMS·천년경영·Desktop Agent 상태는 확인 대기입니다.
        </p>
      </section>}
    </section>
  );
};

export default ErpBridgePanel;
